import {
  Checkbox,
  DropdownMenu,
  MenuItem,
  useClickOutside,
  useDropdownDirection,
} from '@consigli/facade';
import { VersionCompareReviewStatus } from '@consigli/types';
import clsx from 'clsx';
import { FC, SyntheticEvent, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiCheck, HiChevronDown } from 'react-icons/hi';

// "Reviewed" for every count/filter means anything other than UNREVIEWED, so the
// dropdown order keeps UNREVIEWED first and the checkbox toggles against it.
const REVIEW_STATUS_ORDER: VersionCompareReviewStatus[] = [
  VersionCompareReviewStatus.UNREVIEWED,
  VersionCompareReviewStatus.REVIEWED,
  VersionCompareReviewStatus.RESOLVED,
  VersionCompareReviewStatus.NOT_RELEVANT,
];

const REVIEW_STATUS_LABEL_KEYS: Record<VersionCompareReviewStatus, string> = {
  [VersionCompareReviewStatus.UNREVIEWED]: 'version-compare.reconciliation.review.unreviewed',
  [VersionCompareReviewStatus.REVIEWED]: 'version-compare.reconciliation.review.reviewed',
  [VersionCompareReviewStatus.RESOLVED]: 'version-compare.reconciliation.review.resolved',
  [VersionCompareReviewStatus.NOT_RELEVANT]: 'version-compare.reconciliation.review.not-relevant',
};

const REVIEW_STATUS_PILL: Record<VersionCompareReviewStatus, string> = {
  [VersionCompareReviewStatus.UNREVIEWED]: 'border-gray-300 bg-white text-gray-600',
  [VersionCompareReviewStatus.REVIEWED]: 'border-blue-200 bg-blue-50 text-blue-700',
  [VersionCompareReviewStatus.RESOLVED]: 'border-green-200 bg-green-50 text-green-700',
  [VersionCompareReviewStatus.NOT_RELEVANT]: 'border-gray-200 bg-gray-100 text-gray-500',
};

interface ReviewCellProps {
  id: number;
  value: VersionCompareReviewStatus;
  onChange: (status: VersionCompareReviewStatus) => void;
  /** Which edge the status menu anchors to. Table cells open right; the narrow
      preview sidebar opens left so the menu doesn't overflow the panel. */
  align?: 'left' | 'right';
}

export const ReviewCell: FC<ReviewCellProps> = ({ id, value, onChange, align = 'right' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const direction = useDropdownDirection({ containerRef, isOpen: open, dropdownHeight: 200 });

  const closeDropdown = useCallback(() => setOpen(false), []);
  useClickOutside({ ref: containerRef, onClickOutside: closeDropdown, enabled: open });

  // The whole cell lives inside a row whose click/keydown opens the PDF panel, so
  // swallow both here to keep checkbox/dropdown interactions from selecting the row.
  const stopPropagation = useCallback((event: SyntheticEvent) => event.stopPropagation(), []);

  const handleCheckbox = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(
        event.target.checked
          ? VersionCompareReviewStatus.REVIEWED
          : VersionCompareReviewStatus.UNREVIEWED,
      );
    },
    [onChange],
  );

  const handleSelect = useCallback(
    (status: VersionCompareReviewStatus) => {
      onChange(status);
      setOpen(false);
    },
    [onChange],
  );

  const checked = value !== VersionCompareReviewStatus.UNREVIEWED;
  const label = t(REVIEW_STATUS_LABEL_KEYS[value]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      className="relative flex flex-wrap items-center justify-center gap-2"
      onClick={stopPropagation}
      onKeyDown={stopPropagation}
    >
      <Checkbox id={`review-${id}`} checked={checked} onChange={handleCheckbox} ariaLabel={label} />
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={clsx(
          'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
          REVIEW_STATUS_PILL[value],
        )}
      >
        {label}
        <HiChevronDown size={14} />
      </button>
      {/* `fc:w-40` is the one width facade actually generates for its prefixed
          layer (its own default); other fc:w-* strings passed from app code are
          never emitted, so the menu would collapse to its container and wrap. */}
      <DropdownMenu isOpen={open} direction={direction} align={align} width="fc:w-40">
        {REVIEW_STATUS_ORDER.map((status) => (
          <MenuItem
            key={status}
            selected={status === value}
            onClick={() => handleSelect(status)}
            icon={
              status === value ? (
                <HiCheck className="text-green-600" size={16} />
              ) : (
                <span className="inline-block w-4" />
              )
            }
          >
            {t(REVIEW_STATUS_LABEL_KEYS[status])}
          </MenuItem>
        ))}
      </DropdownMenu>
    </div>
  );
};
